import React, { useState, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Chip, IconButton,
  CircularProgress, Alert, Table, TableHead, TableRow, TableCell, TableBody, Collapse,
} from '@mui/material';
import {
  Search as SearchIcon,
  KeyboardArrowDown as DownIcon,
  KeyboardArrowRight as RightIcon,
  Star as ProducedIcon,
  CallMade as OutIcon,
  CallReceived as InIcon,
  HourglassTop as PendingIcon,
  LocalShipping as ShipIcon,
} from '@mui/icons-material';
import DateFilterBar, { DEFAULT_RANGE } from '../components/DateFilterBar';
import { fetchLotTrace, exportExcel, todayStr } from '../utils/reportsApi';

// ============================================================================
//  LotTraceabilityReport
// ============================================================================
const STATUS_COLORS = {
  'PRODUCED':         { bg: '#dcfce7', fg: '#15803d', dot: '#16a34a' },
  'REPACK-OUT':       { bg: '#ffedd5', fg: '#9a3412', dot: '#ea580c' },
  'REPACK-IN':        { bg: '#dbeafe', fg: '#1d4ed8', dot: '#1d4ed8' },
  'RE-PRINTING':      { bg: '#fce7f3', fg: '#be185d', dot: '#be185d' },
  'RE-PROCESS':       { bg: '#f3e8ff', fg: '#7e22ce', dot: '#7e22ce' },
  'REPROCESS-IN':     { bg: '#ddd6fe', fg: '#6d28d9', dot: '#6d28d9' },
  'QC SAMPLE':        { bg: '#cffafe', fg: '#0e7490', dot: '#0e7490' },
  'QC-SAMPLE-IN':     { bg: '#a5f3fc', fg: '#155e75', dot: '#0891b2' },
  'SHIPMENT-PENDING': { bg: '#fef3c7', fg: '#92400e', dot: '#f59e0b' },
  'SHIPPED':          { bg: '#ede9fe', fg: '#5b21b6', dot: '#5b21b6' },
  'EXCESS-RETURNED':  { bg: '#fee2e2', fg: '#991b1b', dot: '#dc2626' },
};

function StatusPill({ status }) {
  const c = STATUS_COLORS[status] || { bg: '#f1f5f9', fg: '#475569', dot: '#94a3b8' };
  return (
    <Chip label={status} size="small" sx={{
      bgcolor: c.bg, color: c.fg, fontWeight: 700, fontSize: 10.5,
      height: 22, '& .MuiChip-label': { px: 1.25 },
    }} />
  );
}

function AgeBadge({ days }) {
  let bg, fg;
  if (days <= 7)       { bg = '#dcfce7'; fg = '#15803d'; }
  else if (days <= 14) { bg = '#fef3c7'; fg = '#92400e'; }
  else                 { bg = '#fee2e2'; fg = '#991b1b'; }
  return (
    <Box component="span" sx={{
      display: 'inline-block', bgcolor: bg, color: fg,
      fontWeight: 700, fontSize: 11, fontFamily: 'JetBrains Mono, monospace',
      px: 1, py: 0.25, borderRadius: 0.5,
    }}>
      {days}d
    </Box>
  );
}

function TimelineEvent({ entry }) {
  // Map change_logs action to status-like display
  const actionToStatus = (action) => {
    if (action === 'CREATE')                    return 'PRODUCED';
    if (action === 'BULK_RETURN' ||
        action === 'BULK_RETURN_SPLIT_TO')      return 'REPACK-IN';
    if (action === 'BULK_SHIPMENT' ||
        action === 'BULK_SHIPMENT_SPLIT_TO')    return 'SHIPMENT-PENDING';
    if (action === 'GATEOUT_APPROVED')          return 'SHIPPED';
    if (action === 'GATEOUT_REJECTED')          return 'CANCELLED';
    if (action === 'STATUS_CHANGE')             return 'REPACK-OUT';
    return 'PRODUCED';
  };
  const status = actionToStatus(entry.action);
  const c = STATUS_COLORS[status] || STATUS_COLORS['PRODUCED'];

  // Build a human-readable detail line from `details` JSON
  const buildDetail = () => {
    const d = entry.details || {};
    const parts = [];
    if (d.gateout_no || d.fcl_no) parts.push(d.gateout_no || d.fcl_no);
    if (d.target_status)         parts.push('→ ' + d.target_status);
    if (d.bin)                    parts.push('BIN ' + d.bin);
    if (d.cartons)                parts.push(d.cartons + ' ctn');
    if (d.from_pallet_id)         parts.push('split from pallet ' + d.from_pallet_id);
    if (d.new_pallet_id)          parts.push('new pallet ' + d.new_pallet_id);
    if (d.reason)                 parts.push('"' + d.reason + '"');
    return parts.join(' · ') || (entry.action || '—');
  };

  return (
    <Box sx={{ display: 'flex', gap: 1.5, pb: 1.75, position: 'relative' }}>
      <Box sx={{
        position: 'absolute', left: 14, top: 30, bottom: -4, width: 2, bgcolor: '#e2e8f0',
      }} />
      <Box sx={{
        width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: c.dot, color: 'white', zIndex: 1,
        fontSize: 14, fontWeight: 700,
      }}>
        {status === 'PRODUCED'         ? '★' :
         status === 'REPACK-OUT'       ? '↗' :
         status === 'REPACK-IN'        ? '↩' :
         status === 'SHIPMENT-PENDING' ? '⏳' :
         status === 'SHIPPED'          ? '🚚' : '•'}
      </Box>
      <Box sx={{ flex: 1, pb: 0.75 }}>
        <Box sx={{ fontSize: 12.5, fontWeight: 700 }}>
          <StatusPill status={status} />
          <Box component="span" sx={{ ml: 1, fontSize: 11.5, color: '#64748b', fontWeight: 400 }}>
            by {entry.by}
          </Box>
        </Box>
        <Box sx={{ fontSize: 11.5, color: '#64748b', mt: 0.25 }}>
          <strong>{entry.action}</strong> · {buildDetail()}
        </Box>
        <Box sx={{
          fontSize: 11, color: '#94a3b8', mt: 0.25,
          fontFamily: 'JetBrains Mono, monospace',
        }}>
          {entry.date}
        </Box>
      </Box>
    </Box>
  );
}

export default function LotTraceabilityReport({ user, showToast }) {
  const [searchLot, setSearchLot] = useState('');
  const [dateFilter, setDateFilter] = useState(DEFAULT_RANGE);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});

  const handleSearch = useCallback(async () => {
    if (!searchLot.trim()) {
      setError('Please enter a lot number');
      return;
    }
    setLoading(true); setError(''); setData(null); setExpanded({});
    const res = await fetchLotTrace(searchLot.trim());
    if (res.success) setData(res.data);
    else setError(res.error);
    setLoading(false);
  }, [searchLot]);

  const handleExport = () => {
    if (!data) return;
    const headers = ['Pallet ID', 'Status', 'Cartons', 'Total KG', 'Current BIN',
                     'Gate-Out No', 'Prior GO No', 'Age (days)'];
    const rows = data.pallets.map(p => [
      p.pallet_id, p.status, p.cartons, p.total_kgs,
      p.cs1 ? `CS1 ${p.cs1}` : '—',
      p.gateout_no || '', p.prior_gateout_no || '', p.age_days,
    ]);
    const historyHeaders = ['Pallet ID', 'Date', 'Action', 'By', 'Details'];
    const historyRows = [];
    data.pallets.forEach(p => {
      p.history.forEach(h => {
        historyRows.push([
          p.pallet_id, h.date, h.action, h.by,
          h.details ? JSON.stringify(h.details) : '',
        ]);
      });
    });
    exportExcel({
      filename: `lot_${data.lot_no}_traceability_${todayStr()}.xlsx`,
      sheets: [
        { name: 'Pallets', headers, rows },
        { name: 'History', headers: historyHeaders, rows: historyRows },
      ],
    });
    showToast?.({ message: '📥 Excel exported', severity: 'success' });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
          🔍 Lot Traceability Report
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>
          Search any lot number to see every pallet's full journey from production to shipment
        </Typography>
      </Box>

      <DateFilterBar
        value={dateFilter}
        onChange={setDateFilter}
        onExport={data ? handleExport : null}
        exportLabel="Export Lot Trace"
      />

      {/* SEARCH BAR */}
      <Paper sx={{
        p: 2, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap',
        background: 'linear-gradient(90deg, #f5edf7, #ede9fe)',
      }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>
          Lot No:
        </Typography>
        <TextField
          size="small"
          value={searchLot}
          onChange={(e) => setSearchLot(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
          placeholder="Enter lot number, e.g. 2853"
          sx={{ minWidth: 240, '& input': { fontSize: 14 } }}
        />
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={handleSearch}
          disabled={loading}
          sx={{
            background: 'linear-gradient(90deg, #C0392B, #8E44AD, #1F618D)',
            color: 'white', fontWeight: 700, textTransform: 'none',
          }}
        >
          {loading ? 'Searching…' : 'Search'}
        </Button>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="info">{error}</Alert>
      )}

      {data && !loading && (
        <>
          {/* LOT SUMMARY */}
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5, flexWrap: 'wrap' }}>
              <Box component="span" sx={{
                fontFamily: 'JetBrains Mono, monospace', fontWeight: 700,
                color: '#1F618D', fontSize: 18, bgcolor: '#dbeafe',
                px: 1.25, py: 0.25, borderRadius: 0.5,
              }}>
                Lot {data.lot_no}
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{data.meta.product}</Typography>
              <Typography sx={{ color: '#64748b' }}>
                · {data.meta.grade} · {data.meta.pack_size}
              </Typography>
              <Typography sx={{ ml: 'auto', fontSize: 12, color: '#64748b' }}>
                {data.meta.brand} · Produced {data.meta.production_date} {data.meta.shift}
              </Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 1.25 }}>
              <StatTile label="Total Pallets" value={data.summary.totalPallets} />
              <StatTile label="In CS Now" value={data.summary.inStock} bg="#dcfce7" fg="#15803d" />
              <StatTile label="Out for Processing" value={data.summary.outForProcessing} bg="#ffedd5" fg="#9a3412" />
              <StatTile label="Shipped" value={data.summary.shipped} bg="#ede9fe" fg="#5b21b6" />
              <StatTile label="Total Cartons" value={data.summary.totalCartons.toLocaleString()} />
            </Box>
          </Paper>

          {/* PALLETS TABLE */}
          <Box sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: '#475569', textTransform: 'uppercase', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            📦 Pallets in this Lot
            <Box component="span" sx={{ bgcolor: '#f5edf7', color: '#8E44AD', fontSize: 10.5, px: 1, borderRadius: 5, fontWeight: 700 }}>
              {data.pallets.length}
            </Box>
          </Box>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell>Pallet ID</TableCell>
                  <TableCell align="right">Cartons</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Current BIN</TableCell>
                  <TableCell align="right">Age</TableCell>
                  <TableCell align="right">Events</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.pallets.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#64748b' }}>
                      No pallets found for this lot.
                    </TableCell>
                  </TableRow>
                )}
                {data.pallets.map(p => (
                  <React.Fragment key={p.id}>
                    <TableRow hover sx={{ cursor: 'pointer' }}
                              onClick={() => setExpanded(prev => ({ ...prev, [p.id]: !prev[p.id] }))}>
                      <TableCell>
                        <IconButton size="small">
                          {expanded[p.id] ? <DownIcon /> : <RightIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <strong style={{ fontFamily: 'JetBrains Mono, monospace', color: '#1F618D' }}>
                          {p.pallet_id}
                        </strong>
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {p.cartons}
                      </TableCell>
                      <TableCell><StatusPill status={p.status} /></TableCell>
                      <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11.5, color: '#64748b' }}>
                        {p.cs1 ? `CS1 ${p.cs1}` : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <AgeBadge days={p.age_days} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 11.5, color: '#64748b' }}>
                        {p.history.length} event(s)
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ p: 0, borderBottom: 'none' }}>
                        <Collapse in={expanded[p.id]} unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: '#fafbfc' }}>
                            {p.history.length === 0 ? (
                              <Typography sx={{ fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>
                                No detailed history recorded for this pallet.
                              </Typography>
                            ) : (
                              p.history.map((h, i) => <TimelineEvent key={i} entry={h} />)
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {!data && !loading && !error && (
        <Alert severity="info">
          Enter a lot number above and click Search to see the full traceability report.
        </Alert>
      )}
    </Box>
  );
}

function StatTile({ label, value, bg = '#f8fafc', fg = '#0f172a' }) {
  return (
    <Box sx={{ bgcolor: bg, borderRadius: 1, p: 1.25 }}>
      <Typography sx={{ fontSize: 10.5, color: '#64748b', fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', color: fg }}>
        {value}
      </Typography>
    </Box>
  );
}
